import { Controller, useForm, useWatch } from 'react-hook-form';
import { t } from '@app/i18n';
import { useAccessControl } from '@app/main/AccessControl/AccessControl.api';
import { agentState } from '@app/main/Resources/resourceVocab';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import {
  Button,
  Field,
  FieldGrid,
  LoadingButton,
  SearchCombobox,
  TextArea,
  type ComboOption,
} from '@zarya/ui';
import {
  claimsExample,
  claimsPlaceholder,
  defaultMethod,
  methodOfTheTarget,
  methodOptions,
  pairValue,
  subjectModeOptions,
  targetModeOptions,
  type SimulatorFormValues,
  type TargetMode,
} from './SimulatorModel';
import { useSimulatorValidation } from './useFormValidation';
import s from './Simulator.module.css';

export interface SimulatorFormProps {
  defaultValues: SimulatorFormValues;
  /**
   * As claims de `defaultValues` vieram de uma decisão (aim-78s, **Reproduzir no simulador** em T17). A nota que diz
   * isso fica enquanto o texto é o que a decisão mandou: editado, ele já não é o da decisão.
   */
  claimsFromDecision?: boolean;
  /** Identidades com contrato ativo — as únicas simuláveis. */
  identidades: ComboOption[];
  pending?: boolean;
  onSubmit: (valores: SimulatorFormValues) => void;
}

/**
 * Card **Chamada** de T18 (FR-100).
 *
 * A chamada (identidade, método, tool) e a PESSOA. O protótipo tinha, além
 * disso, `context.*` (`amount`, `outage_confirmed`, `fraud_risk`), que saiu no
 * corte do PRD §4 porque o contrato não o aceita (L13 §C.i): oferecer o controle
 * seria deixar o operador ajustar `amount` e ver o veredito não mudar.
 *
 * O ALVO TEM DOIS MODOS DESDE 09/09 (DEF-29)
 *
 *	**Tool** é o de sempre. **Agente (A2A)** troca o campo da tool por um combobox
 *	do agente alvo, e o corpo leva `agent` no lugar de `tool` — exatamente um dos
 *	dois, como o contrato exige (422 `toolOrAgentNotBoth`).
 *
 *	Existe porque, até 08/09, perguntar «`atendimento` pode chamar `cobranca`?» só
 *	era possível escrevendo `cobranca` no campo `tool`, e a resposta vinha correta
 *	e inútil: DENY pelo eixo workload, sobre um contrato de tool que a chamada nem
 *	citava. Com a delegação virando capability, ela responde pelos mesmos dois
 *	eixos — e o resultado desenha o mesmo bloco por eixo das tools.
 *
 *	O **Método** continua na tela nos dois modos: ele viaja ao PDP também na
 *	delegação, então escondê-lo esconderia um parâmetro da pergunta.
 *
 * A PESSOA TEM DOIS MODOS DESDE 04/09, E O SEGUNDO NÃO É UM ATALHO
 *
 *	O modo **JWT** é o de sempre: o token real, colado. O modo **Claims (JSON)** é
 *	o objeto de claims que a pessoa teria — o backend monta com ele um JWT não
 *	assinado e manda como sujeito. Foi pedido (mrolim) porque quem opera tem um
 *	perfil na mão, não um token: sem isso, a metade condicional das políticas — a
 *	que depende de claim — não tinha como ser simulada, que é justamente a metade
 *	cujo veredito ninguém prevê de cabeça.
 *
 *	Não é «sujeito sintético» no sentido que o corte do PRD recusou. Aquele
 *	inventava um campo que o middleware não tem; este usa o ÚNICO campo que ele
 *	tem — `token` — e é a mesma montagem que a lente «Ver como» de T19 já fazia.
 *
 *	O preço está dito na tela, não escondido: contra um PDP que valide assinatura
 *	(o Rego do lab valida) o sujeito montado é recusado, e a resposta é DENY por
 *	token inválido. É verdade sobre o mundo, e a tela a diz antes de o operador
 *	clicar.
 *
 * MODO, E NÃO DOIS CAMPOS
 *
 *	Os dois sujeitos juntos são 422 no contrato (`tokenOrClaimsNotBoth`). Um
 *	formulário que aceitasse os dois só descobriria isso depois do POST. Trocar de
 *	modo, porém, **não apaga** o outro campo: o que não está no modo escolhido não
 *	viaja, e voltar devolve o que o operador tinha digitado.
 */
export function SimulatorForm({
  defaultValues,
  claimsFromDecision = false,
  identidades,
  pending = false,
  onSubmit,
}: SimulatorFormProps) {
  const { control, handleSubmit, formState, getValues, setValue } = useForm<SimulatorFormValues>({
    defaultValues,
    ...useSimulatorValidation(),
  });

  // PRD §4 T18: a tool é um combobox com as tools do catálogo (nome no PDP). A
  // lista é a da identidade escolhida (a mesma que T14 mostra); `creatable`
  // continua aceitando um nome fora dela — o PDP responde DENY, e isso também é
  // uma resposta válida (fidelidade #B19).
  //
  // Each option is the PAIR server/tool (decision 13; prototype §5.6): the same name in two servers is two
  // questions to the PDP, and the option says which one is asked.
  const identityChosen = useWatch({ control, name: 'identityId' });
  const subjectMode = useWatch({ control, name: 'subjectMode' });
  const targetMode = useWatch({ control, name: 'targetMode' });
  const claims = useWatch({ control, name: 'claims' });
  const fromDecision = claimsFromDecision && subjectMode === 'claims' && claims === defaultValues.claims;
  const access = useAccessControl(identityChosen || null);
  const tools = Array.from(
    new Set(
      (access.data?.tools ?? []).map((tool) => pairValue(tool.resource_gateway_id, tool.tool_name)),
    ),
  ).sort();
  // Os alvos possíveis da CHAMADORA — a mesma lista que Identity Access › Saída
  // A2A oferece, e a mesma leitura que já foi buscada para as tools. Desde a fase 2 o alvo é o recurso agente e a
  // chamada o nomeia pelo CAMINHO no gateway (`/a2a/<caminho>/`). `creatable` continua aceitando um caminho fora
  // dela: o PDP responde DENY, e isso também é resposta.
  //
  // Cada opção é o recurso agente com o site (só com mais de um, regra do protótipo) e, na dica, o que muda a
  // resposta: `externo`, `sem rota`, `sem destino confiável` (prancha `Simulador` aprovada em 03/10).
  const multiSite = useMultiSite();
  const agentOptions: ComboOption[] = (access.data?.a2a_out ?? [])
    .slice()
    .sort((a, b) => a.path.localeCompare(b.path))
    .map((target) => ({
      value: target.path,
      label: multiSite && target.site_slug ? `${target.name} · ${target.site_slug}` : target.name,
      hint:
        target.scope === 'external'
          ? t('resources.binding.external')
          : target.state !== 'ready'
            ? agentState(target.state).rotulo
            : undefined,
      mono: true,
    }));
  const agents = agentOptions.map((option) => String(option.value));

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FieldGrid>
        <Controller
          control={control}
          name="identityId"
          render={({ field, fieldState }) => (
            <Field
              label={t('simulator.form.identity.label')}
              span={6}
              required
              error={fieldState.error?.message}
              tip={t('simulator.form.identity.tip')}
            >
              {/* `className` do `Field` é a do `<input>`: no combobox ela iria
                  para o wrapper e desenharia uma borda em cima da outra. */}
              {({ id, className: _classeDeInput, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('simulator.form.identity.label')}
                  options={identidades}
                  value={field.value || null}
                  placeholder={t('simulator.form.identity.placeholder')}
                  emptyMessage={t('simulator.form.identity.empty')}
                  invalid={Boolean(fieldState.error)}
                  onChange={(valor) => field.onChange(valor ?? '')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="rpcMethod"
          render={({ field }) => (
            <Field
              label={t('simulator.form.method.label')}
              span={6}
              tip={t('simulator.form.method.tip')}
            >
              {({ id, className: _classeDeInput, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('simulator.form.method.label')}
                  options={methodOptions(targetMode).map((metodo) => ({
                    value: metodo.value,
                    label: metodo.label,
                    hint: metodo.hint,
                    mono: true,
                  }))}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? defaultMethod(targetMode))}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="targetMode"
          render={({ field }) => (
            <Field
              label={t('simulator.form.target.label')}
              span={4}
              tip={t('simulator.form.target.tip')}
            >
              {({ id, className: _classeDeInput, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('simulator.form.target.label')}
                  options={targetModeOptions().map((modo) => ({
                    value: modo.value,
                    label: modo.label,
                    hint: modo.hint,
                  }))}
                  value={field.value}
                  onChange={(valor) => {
                    const target = (valor ?? 'tool') as TargetMode;
                    field.onChange(target);
                    if (!methodOfTheTarget(getValues('rpcMethod'), target)) {
                      setValue('rpcMethod', defaultMethod(target));
                    }
                  }}
                />
              )}
            </Field>
          )}
        />

        {targetMode === 'agent' ? (
          <Controller
            control={control}
            name="agent"
            render={({ field, fieldState }) => (
              <Field
                label={t('simulator.form.agent.label')}
                span={8}
                required
                error={fieldState.error?.message}
                help={t('simulator.form.agent.help')}
              >
                {({ id, className: _classeDeInput, ...aria }) => (
                  <SearchCombobox
                    id={id}
                    {...aria}
                    label={t('simulator.form.agent.label')}
                    options={[
                      ...agentOptions,
                      ...(field.value && !agents.includes(field.value)
                        ? [{ value: field.value, label: field.value, mono: true }]
                        : []),
                    ]}
                    value={field.value || null}
                    placeholder={t('simulator.form.agent.placeholder')}
                    emptyMessage={t('simulator.form.agent.empty')}
                    creatable
                    onCreate={(texto) => field.onChange(texto.trim())}
                    invalid={Boolean(fieldState.error)}
                    onChange={(valor) => field.onChange(valor ?? '')}
                  />
                )}
              </Field>
            )}
          />
        ) : (
          <Controller
            control={control}
            name="tool"
            render={({ field, fieldState }) => (
              <Field
                label={t('simulator.form.tool.label')}
                span={8}
                required
                error={fieldState.error?.message}
                help={t('simulator.form.tool.help')}
              >
                {({ id, className: _classeDeInput, ...aria }) => (
                  <SearchCombobox
                    id={id}
                    {...aria}
                    label={t('simulator.form.tool.label')}
                    options={[
                      ...tools.map((tool) => ({ value: tool, label: tool, mono: true })),
                      ...(field.value && !tools.includes(field.value)
                        ? [{ value: field.value, label: field.value, mono: true }]
                        : []),
                    ]}
                    value={field.value || null}
                    placeholder={t('simulator.form.tool.placeholder')}
                    emptyMessage={t('simulator.form.tool.empty')}
                    creatable
                    onCreate={(texto) => field.onChange(texto.trim())}
                    invalid={Boolean(fieldState.error)}
                    onChange={(valor) => field.onChange(valor ?? '')}
                  />
                )}
              </Field>
            )}
          />
        )}

        <Controller
          control={control}
          name="subjectMode"
          render={({ field }) => (
            <Field
              label={t('simulator.form.subject.label')}
              span={12}
              tip={t('simulator.form.subject.tip')}
            >
              {({ id, className: _classeDeInput, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('simulator.form.subject.label')}
                  options={subjectModeOptions().map((modo) => ({
                    value: modo.value,
                    label: modo.label,
                    hint: modo.hint,
                  }))}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? 'token')}
                />
              )}
            </Field>
          )}
        />

        {subjectMode === 'claims' ? (
          <Controller
            control={control}
            name="claims"
            render={({ field, fieldState }) => (
              <Field
                label={t('simulator.form.claims.label')}
                span={12}
                required
                error={fieldState.error?.message}
                help={t('simulator.form.claims.help')}
                tip={t('simulator.form.claims.tip')}
              >
                {(props) => (
                  <TextArea
                    {...props}
                    {...field}
                    mono
                    rows={5}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder={claimsPlaceholder()}
                  />
                )}
              </Field>
            )}
          />
        ) : (
          <Controller
            control={control}
            name="token"
            render={({ field }) => (
              <Field
                label={t('simulator.form.token.label')}
                span={12}
                help={t('simulator.form.token.help')}
                tip={t('simulator.form.token.tip')}
              >
                {(props) => (
                  <TextArea
                    {...props}
                    {...field}
                    mono
                    rows={3}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder={t('simulator.form.token.placeholder')}
                  />
                )}
              </Field>
            )}
          />
        )}
      </FieldGrid>

      {fromDecision ? (
        <p className={s.nota} data-testid="simulator-claims-from-decision">
          {t('simulator.form.claims.fromDecision')}
        </p>
      ) : null}

      <div className={s.acoes}>
        {/* **Exemplo** escreve um objeto que funciona, no lugar de uma frase que o
            descreve: quem nunca viu o formato aprende com uma tecla, e o placeholder
            desaparece assim que se digita. Fica à esquerda do primário — um botão
            primário por seção (FR-129). */}
        {subjectMode === 'claims' ? (
          <Button
            type="button"
            variant="out"
            icon="plus"
            onClick={() =>
              // `shouldValidate`: o exemplo vale, e o **Avaliar** tem de acender na
              // mesma tecla — escrever um JSON correto e continuar com o botão apagado
              // faria o operador procurar erro onde não há.
              setValue('claims', claimsExample(), { shouldValidate: true, shouldDirty: true })
            }
          >
            {t('simulator.form.example')}
          </Button>
        ) : null}
        <LoadingButton
          type="submit"
          icon="play"
          loading={pending}
          loadingLabel={t('simulator.form.submit.loading')}
          disabled={!formState.isValid}
        >
          {t('simulator.form.submit')}
        </LoadingButton>
      </div>
    </form>
  );
}
